// src/components/SidebarDynamicTypes.tsx
//
// Sous-composant réutilisable pour la Sidebar : liste les types d'opération
// dynamiques (configurés via DevMode) qui ciblent un module donné, et génère
// un lien vers la page de saisie générique `/demandes/nouvelle/:typeCode`.
//
// Usage plat (comportement historique) :
//   <SidebarDynamicTypes moduleCode="caisse" closeMobile={...} />
//
// Usage groupé par catégorie (Recouvrement, Réclamation…) :
//   <SidebarDynamicTypes moduleCode="electricite" groupByCategorie closeMobile={...} />
//
// FILTRAGE — un type apparaît UNIQUEMENT si TOUTES les conditions sont vraies :
//   1. Type actif (t.actif !== false)
//   2. Type non-système (sauf si includeSystem=true) — les types système ont
//      déjà leur lien dédié dans la sidebar (ex: COURRIER_ENTRANT en haut).
//   3. Type cible le moduleCode courant via t.moduleCodes
//      (PAS de fallback "universel" : on EXIGE moduleCodes non vide pour éviter
//      qu'un type créé par erreur apparaisse partout)
//   4. Type cible AU MOINS UN module activé sur l'entité du user (package SaaS)
//      → garantit qu'un patronat ne voit pas les types caisse, ni inversement.
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Plus, Sparkles, ChevronRight, ChevronDown,
  Wallet, AlertTriangle, RefreshCw, FileX, ArrowRightLeft,
  ShieldAlert, Handshake,
  Plug, FilePen, XCircle, ClipboardCheck,
  Home, Gauge, GitMerge, FileEdit,
  BarChart2, FolderOpen, Scale, TrendingUp, LayoutDashboard,
  Radar, Receipt, Lock, Zap,
} from 'lucide-react';
import { useTypesOperation } from '../hooks/useTypesOperation';
import { useAuth } from '../context/AuthContext';

interface Props {
  /** Code du module au sens du référentiel `module` (decaissement_workflow, rh, mon_espace, courrier…) */
  moduleCode: string;
  /** Callback de fermeture du menu mobile lors du clic. */
  closeMobile?: () => void;
  /** Inclure aussi les types système (par défaut faux pour ne pas dupliquer). */
  includeSystem?: boolean;
  /** Grouper les types par catégorie (Recouvrement, Réclamation…) avec sections dépliables. */
  groupByCategorie?: boolean;
}

/** Métadonnées d'affichage par valeur de `categorie`.
 *  L'ordre des clés détermine l'ordre d'affichage dans le sidebar groupé. */
const CATEGORIE_META: Record<string, { label: string; icon: React.ComponentType<any>; color: string }> = {
  // ── Travaux & Branchements (priorité haute pour EEC) ─────────────────────
  travaux_neufs:       { label: 'Travaux neufs',                    icon: Home,          color: 'text-blue-700'   },
  travaux_compteur:    { label: 'Gestion compteur',                 icon: Gauge,         color: 'text-cyan-700'   },
  travaux_branchement: { label: 'Travaux sur branchement existant', icon: GitMerge,      color: 'text-teal-600'   },
  branchement:         { label: 'Branchements & abonnements',       icon: Plug,          color: 'text-yellow-600' },
  // ── Contrat & Résiliation ────────────────────────────────────────────────
  contrat:             { label: 'Modifications contractuelles',     icon: FileEdit,      color: 'text-slate-700'  },
  modification:        { label: 'Modifications de contrat',         icon: FilePen,       color: 'text-slate-600'  },
  resiliation:         { label: 'Résiliation',                      icon: XCircle,       color: 'text-red-500'    },
  // ── Recouvrement & Réclamations ──────────────────────────────────────────
  recouvrement:        { label: 'Recouvrement',                     icon: Wallet,        color: 'text-orange-600' },
  reclamation:         { label: 'Réclamation',                      icon: AlertTriangle, color: 'text-amber-600'  },
  remise_service:      { label: 'Remise en service',                icon: RefreshCw,     color: 'text-green-600'  },
  // ── Facturation & Relevé ─────────────────────────────────────────────────
  facturation:         { label: 'Facturation',                      icon: FileX,         color: 'text-blue-600'   },
  releve:              { label: 'Relevés & interventions',          icon: ClipboardCheck,color: 'text-indigo-600' },
  // ── Commercial & Divers ──────────────────────────────────────────────────
  accord_commercial:   { label: 'Accords commerciaux',              icon: Handshake,     color: 'text-emerald-600'},
  fraude:              { label: 'Pertes non techniques',            icon: ShieldAlert,   color: 'text-red-600'    },
  transfert:           { label: 'Transfert',                        icon: ArrowRightLeft,color: 'text-slate-600'  },
};

function getCategorieMeta(code: string) {
  return CATEGORIE_META[code] ?? { label: code.replace(/_/g, ' '), icon: Sparkles, color: 'text-purple-600' };
}

interface StaticNavLink { label: string; to: string; Icon: React.ComponentType<{ className?: string }> }

/** Section dépliable pour une catégorie dans le mode groupé. */
const CategorieSection: React.FC<{
  code: string;
  types: { code: string; libelle: string; description?: string }[];
  closeMobile?: () => void;
  staticLinks?: StaticNavLink[];
}> = ({ code, types, closeMobile, staticLinks }) => {
  const location = useLocation();
  const { label, icon: Icon, color } = getCategorieMeta(code);
  const storageKey = `sidebar_cat_open_${code}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      // Par défaut ouvert si l'URL active correspond à un type de cette catégorie
      if (v === null) return types.some(t => location.pathname === `/demandes/nouvelle/${t.code}`);
      return v === '1';
    } catch { return false; }
  });

  const toggle = () => setOpen(prev => {
    const next = !prev;
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch {}
    return next;
  });

  return (
    <>
      {/* En-tête de catégorie — dépliable */}
      <button
        onClick={toggle}
        className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-left transition-colors hover:bg-gray-50 group`}
      >
        <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${color}`}>
          <Icon className="w-3.5 h-3.5" />
          <span>{label}</span>
          <span className="text-[10px] font-normal opacity-60">({types.length})</span>
        </div>
        {open
          ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
          : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />}
      </button>

      {/* Types de cette catégorie */}
      {open && (
        <div className="ml-3 pl-2 border-l border-gray-200 space-y-0.5">
          {/* Liens statiques de navigation (ex: onglets RecouvrementPage) */}
          {staticLinks && staticLinks.map(sl => {
            const isActive = location.pathname === sl.to.split('?')[0] &&
              (!sl.to.includes('?') || location.search === '?' + sl.to.split('?')[1]);
            return (
              <Link
                key={sl.to}
                to={sl.to}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  isActive
                    ? 'text-purple-600 bg-purple-50 font-medium'
                    : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                }`}
              >
                <sl.Icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="leading-tight line-clamp-2">{sl.label}</span>
              </Link>
            );
          })}
          {staticLinks && staticLinks.length > 0 && types.length > 0 && (
            <div className="border-t border-gray-100 my-1" />
          )}
          {types.map(t => {
            const path = `/demandes/nouvelle/${t.code}`;
            const isActive = location.pathname === path;
            return (
              <Link
                key={t.code}
                to={path}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                title={t.description || `Saisir une demande ${t.libelle}`}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  isActive
                    ? 'text-purple-600 bg-purple-50 font-medium'
                    : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                }`}
              >
                <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="leading-tight line-clamp-2">{t.libelle}</span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
};

const SidebarDynamicTypes: React.FC<Props> = ({
  moduleCode,
  closeMobile,
  includeSystem = false,
  groupByCategorie = false,
}) => {
  const { types, loading } = useTypesOperation();
  const { user, hasModule, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) return null;

  // Modules activés sur l'entité de l'utilisateur (depuis son package SaaS).
  // Si super_admin avec '*' → tous les modules sont implicitement activés.
  const userModules: string[] = ((user as any)?.modulesActifs as string[]) || [];
  const isWildcard = userModules.includes('*');

  const filteredTypes = types
    // 1. Actif
    .filter(t => t.actif !== false)
    // 2. Pas système (sauf override)
    .filter(t => includeSystem || !t.systeme)
    // 3. Le type doit avoir des moduleCodes ET inclure le moduleCode courant
    //    (on n'autorise PAS les types "universels" sans moduleCodes pour éviter
    //    pollution multi-tenant — les types universels apparaissent uniquement
    //    sur les écrans qui les listent explicitement)
    .filter(t =>
      Array.isArray(t.moduleCodes) &&
      t.moduleCodes.length > 0 &&
      t.moduleCodes.includes(moduleCode),
    )
    // 4. Au moins UN moduleCode du type est activé sur l'entité du user
    //    (sécurité multi-tenant : un patronat ne voit pas les types caisse)
    .filter(t =>
      isWildcard ||
      (t.moduleCodes || []).some(mc => hasModule(mc) || userModules.includes(mc)),
    )
    // 5. L'utilisateur doit avoir le droit `access_<code_lower>` sur ce type.
    //    Le droit est auto-créé côté backend à la création/seed du type
    //    (cf. secteur.service.ts). `hasPermission` retourne true pour
    //    admin/super_admin (cf. AuthContext) — pas de régression admin.
    .filter(t => hasPermission('access_' + t.code.toLowerCase()))
    // 6. Masquer les types marqués masquerSidebar:true dans leur metadata
    //    (ex: UTIL_BRANCH_ABO_MT générique, couvert par ses 4 variantes spécifiques)
    .filter(t => !(t as any).metadata?.masquerSidebar);

  if (filteredTypes.length === 0) return null;

  const sorted = filteredTypes
    .slice()
    .sort((a, b) => (a.ordre || 0) - (b.ordre || 0) || a.libelle.localeCompare(b.libelle));

  // ── Mode groupé par catégorie ─────────────────────────────────────────
  if (groupByCategorie) {
    // Grouper par categorie (null/undefined → 'autres')
    const groupMap = new Map<string, typeof sorted>();
    for (const t of sorted) {
      const cat = (t as any).categorie || 'autres';
      if (!groupMap.has(cat)) groupMap.set(cat, []);
      groupMap.get(cat)!.push(t);
    }

    // Trier les catégories : celles connues d'abord (selon leur ordre dans CATEGORIE_META), puis 'autres'
    const knownOrder = Object.keys(CATEGORIE_META);
    const categories = Array.from(groupMap.entries()).sort(([a], [b]) => {
      const iA = knownOrder.indexOf(a);
      const iB = knownOrder.indexOf(b);
      if (a === 'autres') return 1;
      if (b === 'autres') return -1;
      if (iA === -1 && iB === -1) return a.localeCompare(b);
      if (iA === -1) return 1;
      if (iB === -1) return -1;
      return iA - iB;
    });

    const RECOUVREMENT_NAV: StaticNavLink[] = [
      { label: 'Tableau de bord',     to: '/recouvrement?tab=dashboard',   Icon: LayoutDashboard },
      { label: 'Dossiers de relance', to: '/recouvrement?tab=dossiers',    Icon: FolderOpen      },
      { label: 'Résiliations',        to: '/recouvrement?tab=resiliations', Icon: FileX           },
      { label: 'Contentieux',         to: '/recouvrement?tab=contentieux', Icon: Scale           },
      { label: 'Statistiques',        to: '/recouvrement?tab=statistiques', Icon: TrendingUp      },
    ];

    const FRAUDE_NAV: StaticNavLink[] = [
      { label: 'Tableau de bord',       to: '/pertes-non-techniques',                  Icon: LayoutDashboard },
      { label: 'Détecteur de fraude',   to: '/pertes-non-techniques/detection',        Icon: Radar           },
      { label: 'Facturer une fraude',   to: '/pertes-non-techniques/facture-fraude',   Icon: Receipt         },
      { label: 'Parc de scellés',       to: '/stock/scelles',                          Icon: Lock            },
      { label: 'Coupure / dépose impayé', to: '/demandes/nouvelle/UTIL_COUPURE_IMPAYE', Icon: Zap            },
    ];

    return (
      <>
        <div className="border-t border-gray-200 my-2"></div>
        <div className="space-y-1">
          {categories.map(([catCode, catTypes]) => (
            <CategorieSection
              key={catCode}
              code={catCode}
              types={catTypes}
              closeMobile={closeMobile}
              staticLinks={
                catCode === 'recouvrement' ? RECOUVREMENT_NAV :
                catCode === 'fraude'       ? FRAUDE_NAV       :
                undefined
              }
            />
          ))}
        </div>
      </>
    );
  }

  // ── Mode plat (comportement historique) ───────────────────────────────
  return (
    <>
      {/* Séparateur + titre de section */}
      <div className="border-t border-gray-200 my-2"></div>
      <div className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-purple-700">
        <Sparkles className="w-3 h-3" />
        Demandes dynamiques ({sorted.length})
      </div>

      {sorted.map(t => {
        const path = `/demandes/nouvelle/${t.code}`;
        const isActive = location.pathname === path;
        return (
          <Link key={t.code}
            to={path}
            onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
            title={t.description || `Saisir une demande ${t.libelle}`}
            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
              isActive
                ? 'text-purple-600 bg-purple-50 font-medium'
                : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
            }`}>
            <Plus className="h-4 w-4" />
            <span className="truncate">{t.libelle}</span>
          </Link>
        );
      })}
    </>
  );
};

export default SidebarDynamicTypes;
